import { contactUsData } from "store/allData";
import Heading from "components/common/Heading";
import Button from "components/common/Button";
import Social from "components/common/Social";
import SectionWrapper from "components/common/SectionWrapper";
import { ChangeEventHandler, useState } from "react";
import { toast } from "react-toastify";

interface State {
	name: string;
	email: string;
	message: string;
	company: string;
}

type StateKey = keyof State;

const getDefaultState = () => ({
	company: "",
	email: "",
	message: "",
	name: "",
});

const ContactUs: React.FC = () => {
	const [state, setState] = useState(getDefaultState);
	const [loading, setLoading] = useState(false);

	const handleChange: ChangeEventHandler<
		HTMLInputElement | HTMLTextAreaElement
	> = ({ target: { name, value } }) => {
		setState({
			...state,
			[name as StateKey]: value,
		});
	};

	const handleSubmit = async () => {
		setLoading(true);

		try {
			await fetch(`/api/contact-us`, {
				body: JSON.stringify({
					form: state,
				}),
				method: "post",
				headers: {
					"Content-Type": "application-json",
				},
			});

			toast.success(
				"Submitted successfully, We will get back to you as soon as possible!",
			);
			setState(getDefaultState);
		} catch (error) {
			if (
				typeof error === "object" &&
				error !== null &&
				"message" in error &&
				typeof error.message === "string"
			)
				toast.error(error.message);
		} finally {
			setLoading(false);
		}
	};

	const isSubmitDisabled = ![state.email, state.message, state.name].every(
		Boolean,
	);

	return (
		<SectionWrapper className="mx-auto">
			<div
				id="contact-us"
				className="w-full flex flex-col lg:flex-row justify-between gap-14 lg:gap-20 mt-32 mb-24 scroll-mt-32 lg:px-12 xl:px-24"
			>
				{/* left: pitch + direct channels */}
				<div className="flex-1 flex flex-col items-start max-w-xl">
					<span className="eyebrow">Contact us</span>
					<Heading className="text-3xl sm:text-4xl mt-4">
						Let&apos;s talk about your product
					</Heading>
					<p className="text-base text-zinc-400 mt-5 leading-relaxed">
						Tell us about your idea, timeline, and goals. We usually reply
						within one business day — no forms disappearing into the void.
					</p>

					<a
						href="mailto:support@spila.dev"
						className="mt-10 font-heading text-lg font-semibold text-white hover:text-primary-70 duration-300"
					>
						support@spila.dev
					</a>
					<span className="text-xs text-zinc-500 mt-1">
						Prefer email? Write to us directly.
					</span>

					<div className="flex justify-start items-center gap-3 mt-8">
						{contactUsData.map((social) => {
							return (
								<Social
									key={social.href}
									data={social}
									hasTransition={true}
									svgWidth="22px"
									width="44px"
									isSquare
								/>
							);
						})}
					</div>
				</div>

				{/* right: form card */}
				<div className="flex-1 max-w-xl w-full card-surface p-6 sm:p-8">
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
						<Input
							value={state.name}
							onChange={handleChange}
							id="name"
							name="name"
							label="Name"
							placeholder="Jane Doe"
							required
						/>
						<Input
							value={state.email}
							onChange={handleChange}
							id="email"
							name="email"
							label="Email"
							placeholder="jane@company.com"
							required
						/>
					</div>
					<div className="mt-4">
						<Input
							value={state.company}
							onChange={handleChange}
							id="company"
							name="company"
							label="Company (optional)"
							placeholder="Company Inc."
						/>
					</div>

					<div className="mt-4">
						<label
							htmlFor="message"
							className="block mb-2 text-sm font-medium text-zinc-300"
						>
							Message
						</label>
						<textarea
							value={state.message}
							onChange={handleChange}
							id="message"
							name="message"
							placeholder="What are you building?"
							className="input-dark resize-none !h-36"
							required
						/>
					</div>

					<Button
						disabled={isSubmitDisabled || loading}
						clickHandler={handleSubmit}
						variant="primary"
						size={1.5}
						className="w-full mt-6 !rounded-xl"
					>
						{loading ? "Sending..." : "Send message"}
					</Button>
				</div>
			</div>
		</SectionWrapper>
	);
};

export default ContactUs;

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
	label: string;
};

const Input: React.FC<InputProps> = ({ label, ...rest }) => {
	return (
		<div>
			<label
				htmlFor={rest.id}
				className="block mb-2 text-sm font-medium text-zinc-300"
			>
				{label}
			</label>

			<input type="text" {...rest} className="input-dark" />
		</div>
	);
};
